// src/components/User/User.js
import React, { useState, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import SideModal from "../../components/SideModal";
import UserForm from "./Components/UserForm/UserForm";
import UserDataGrid from "./Components/UserDataGrid/UserDataGrid";
import { useDispatch } from "react-redux";
import { useTheme, useMediaQuery } from '@mui/material';
import {
  addUsers,
  updateUsers,
  fetchUsers,
} from "../../store/Users/usersThunks";
import PageTitle from "../../components/PageTitle";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import UserToolbar from "./Components/UserToolbar";
import { useGridApiContext } from "@mui/x-data-grid-pro";
import Divider from "@mui/material/Divider";

function User() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const gridApiRef = useRef(null);

  // Force re-render when screen size changes
  useEffect(() => {
    // This empty effect will trigger a re-render when screen size changes
    // because isSmallScreen and isMediumScreen are dependencies
  }, [isSmallScreen, isMediumScreen]);

  const handleAddUser = () => {
    setSelectedUser(null);
    setOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setOpen(true);
  };

  const handleSubmit = async (formData) => {
    try {
      if (selectedUser) {
        await dispatch(updateUsers({ id: selectedUser.id, ...formData }));
      } else {
        await dispatch(addUsers(formData));
      }
      await dispatch(fetchUsers({ page: 1 }));
      setOpen(false);
      setActiveStep(0);
      setSelectedUser(null);
    } catch (error) {
      console.error("Error submitting user:", error);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    dispatch(fetchUsers({ page: 1, search: query }));
  };

  const handleRefresh = () => {
    dispatch(fetchUsers({ page: 1, search: searchQuery }));
  };

  const handleFilterClick = () => {
    if (gridApiRef.current) {
      gridApiRef.current.showFilterPanel();
    }
  };

  const handleColumnClick = () => {
    if (gridApiRef.current) {
      gridApiRef.current.showPreferences('columns');
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: (theme) => theme.palette.background.default,
      }}
    >
      {/* Fixed header section */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: (theme) => theme.palette.background.paper,
          p: isSmallScreen ? 1 : 1.5,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box 
          sx={{ 
            display: "flex", 
            flexDirection: isSmallScreen ? "column" : "row",
            alignItems: isSmallScreen ? "flex-start" : "center", 
            justifyContent: "space-between",
            gap: isSmallScreen ? 2 : 0
          }}
        >
          <PageTitle 
            title="User Management" 
            subtitle="Manage system users and their permissions"
          />
          <Box 
            sx={{ 
              display: "flex", 
              flexDirection: isSmallScreen ? "column" : "row",
              alignItems: isSmallScreen ? "stretch" : "center", 
              gap: 2,
              width: isSmallScreen ? "100%" : "auto"
            }}
          >
            <UserToolbar 
              onSearch={handleSearch}
              searchQuery={searchQuery}
              onRefresh={handleRefresh}
              onFilterClick={handleFilterClick}
              onColumnClick={handleColumnClick}
              isSmallScreen={isSmallScreen}
            />
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddUser}
              variant="contained"
              size="small"
              fullWidth={isSmallScreen}
              sx={{
                backgroundColor: (theme) => theme.palette.primary.main,
                color: (theme) => theme.palette.common.white,
                textTransform: 'none',
                px: 2,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.primary.dark,
                },
              }}
            >
              Add User
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Content section */}
      <Box
        sx={{
          flex: 1,
          //p: isSmallScreen ? 1 : 2,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            overflow: "hidden",
            //border: (theme) => `1px solid ${theme.palette.divider}`,
            //borderRadius: 2,
            "& .MuiDataGrid-root": {
              border: "none",
              backgroundColor: (theme) => theme.palette.background.paper,
              padding: 0,
              margin: 0,
            },
          }}
        >
          <UserDataGrid 
            onEditUser={handleEditUser} 
            onAddUser={handleAddUser} 
            apiRef={gridApiRef}
            isSmallScreen={isSmallScreen}
          />
        </Paper>
      </Box>

      <SideModal
        open={open}
        onClose={() => setOpen(false)}
        title={selectedUser ? "Edit User" : "Add User"}
      >
        <UserForm
          onSubmit={handleSubmit}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          initialData={selectedUser || {}}
        />
      </SideModal>
    </Box>
  );
}

export default User;
